/**
 * Org-scoped custom executive metrics — server persistence with local fallback.
 */

import { TILE_PLACEMENTS, TILE_PROVIDER_TYPES } from '../config/executiveTileCatalog';
import { executiveApiService } from './executiveApiService';

const STORAGE_PREFIX = 'executive_custom_metrics';
const CACHE_TTL_MS = 30_000;
const MIGRATION_SESSION_PREFIX = 'executive_local_migrated:';

const configCache = new Map();
const inflightConfig = new Map();

function storageKey(organizationId) {
  return `${STORAGE_PREFIX}:${organizationId || 'default'}`;
}

function readLocalStore(organizationId) {
  if (typeof window === 'undefined') return { tiles: [], layout: {}, branding: {} };
  try {
    const raw = localStorage.getItem(storageKey(organizationId));
    if (!raw) return { tiles: [], layout: {}, branding: {} };
    const parsed = JSON.parse(raw);
    return {
      tiles: Array.isArray(parsed.tiles) ? parsed.tiles : [],
      layout: parsed.layout || {},
      branding: parsed.branding || {},
    };
  } catch {
    return { tiles: [], layout: {}, branding: {} };
  }
}

function writeLocalStore(organizationId, data) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(storageKey(organizationId), JSON.stringify(data));
}

function invalidateExecutiveConfigCache(organizationId) {
  if (organizationId == null || organizationId === '') return;
  const key = String(organizationId);
  configCache.delete(key);
  inflightConfig.delete(key);
}

function notifyUpdated(organizationId) {
  invalidateExecutiveConfigCache(organizationId);
  window.dispatchEvent(
    new CustomEvent('executive-custom-metrics-updated', { detail: { organizationId } })
  );
}

function makeId(prefix = 'custom') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function formatMetricValue(value, unit) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  if (unit === 'percent') return `${num}%`;
  if (unit === 'currency') return `$${num.toLocaleString()}`;
  if (unit === 'days') return `${num}d`;
  return num.toLocaleString();
}

function normalizeTile(tile) {
  return {
    id: tile.id,
    label: tile.label,
    value: tile.value != null ? Number(tile.value) : null,
    compareValue: tile.compareValue != null ? Number(tile.compareValue) : null,
    unit: tile.unit || 'count',
    placement: tile.placement || TILE_PLACEMENTS.TOP_KPI,
    scorecardId: tile.scorecardId || null,
    metricLabel: tile.metricLabel || null,
    provider: tile.provider || TILE_PROVIDER_TYPES.MANUAL,
    sourceLabel: tile.sourceLabel || 'Custom data',
    polarity: tile.polarity || 'higher_is_better',
    feedId: tile.feedId || null,
    updatedAt: tile.updatedAt || null,
  };
}

async function migrateLocalToServer(organizationId) {
  const key = String(organizationId);
  if (typeof window !== 'undefined') {
    try {
      if (sessionStorage.getItem(`${MIGRATION_SESSION_PREFIX}${key}`)) return;
    } catch {
      /* ignore sessionStorage errors */
    }
  }

  const local = readLocalStore(organizationId);
  if (!local.tiles.length && !(local.layout && Object.keys(local.layout).length)) {
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(`${MIGRATION_SESSION_PREFIX}${key}`, '1');
      } catch {
        /* ignore */
      }
    }
    return;
  }

  try {
    for (const tile of local.tiles) {
      await executiveApiService.upsertCustomMetric(organizationId, normalizeTile(tile));
    }
    if (local.layout && Object.keys(local.layout).length) {
      await executiveApiService.saveLayout(organizationId, { layout: local.layout });
    }
    localStorage.removeItem(storageKey(organizationId));
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(`${MIGRATION_SESSION_PREFIX}${key}`, '1');
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* keep local until API available */
  }
}

function normalizeSparkline(values) {
  if (!values?.length) return [];
  const nums = values.map(Number).filter((n) => !Number.isNaN(n));
  if (!nums.length) return [];
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  return nums.map((n) => Math.round(20 + ((n - min) / span) * 80));
}

function applyTilesToPayload(payload, tiles) {
  if (!tiles?.length) {
    return { ...payload, customTopKpis: [], customScorecardPatches: {} };
  }

  const topKpiPatches = new Map();
  const customScorecardPatches = {};

  tiles.forEach((tile) => {
    if (tile.placement === TILE_PLACEMENTS.TOP_KPI) {
      const historyValues = (tile.history || []).map((h) => h.value).filter((v) => v != null);
      const priorFromHistory =
        historyValues.length >= 2 ? historyValues[historyValues.length - 2] : null;
      const delta =
        tile.value != null && tile.compareValue != null
          ? Number((tile.value - tile.compareValue).toFixed(1))
          : tile.value != null && priorFromHistory != null
            ? Number((tile.value - priorFromHistory).toFixed(1))
            : null;
      topKpiPatches.set(tile.id, {
        id: tile.id,
        label: tile.label,
        live: tile.value != null,
        value: tile.value,
        unit: tile.unit,
        delta,
        polarity: tile.polarity,
        provider: tile.provider,
        sourceLabel: tile.sourceLabel,
        custom: true,
        dataStatus: tile.value != null ? 'ok' : 'empty',
        history: tile.history || [],
        sparkline: normalizeSparkline(
          tile.sparkline?.length ? tile.sparkline : historyValues
        ),
      });
      return;
    }

    if (tile.placement === TILE_PLACEMENTS.SCORECARD_METRIC && tile.scorecardId) {
      if (!customScorecardPatches[tile.scorecardId]) {
        customScorecardPatches[tile.scorecardId] = [];
      }
      customScorecardPatches[tile.scorecardId].push({
        label: tile.metricLabel || tile.label,
        value: formatMetricValue(tile.value, tile.unit),
        live: tile.value != null,
        custom: true,
        sourceLabel: tile.sourceLabel,
      });
    }
  });

  const scorecards = (payload.scorecards || []).map((card) => {
    const patches = customScorecardPatches[card.id];
    if (!patches?.length) return card;

    const patchedMetrics = [...(card.metrics || [])];
    patches.forEach((patch) => {
      const existingIdx = patchedMetrics.findIndex((m) => m.label === patch.label);
      if (existingIdx >= 0) patchedMetrics[existingIdx] = { ...patchedMetrics[existingIdx], ...patch };
      else patchedMetrics.push(patch);
    });

    return {
      ...card,
      live: card.live || patches.some((p) => p.live),
      metrics: patchedMetrics,
      hasCustomData: true,
    };
  });

  const mergedTopKpis = (payload.topKpis || []).map((kpi) => {
    const patch = topKpiPatches.get(kpi.id);
    if (!patch) return kpi;
    topKpiPatches.delete(kpi.id);
    return {
      ...kpi,
      ...patch,
      id: kpi.id,
      label: kpi.label,
      polarity: patch.polarity || kpi.polarity,
      integrationLabel: patch.live ? undefined : kpi.integrationLabel,
    };
  });
  const extraTopKpis = [...topKpiPatches.values()];

  return {
    ...payload,
    topKpis: [...mergedTopKpis, ...extraTopKpis],
    scorecards,
    customTopKpis: extraTopKpis,
    customScorecardPatches,
    hasCustomMetrics: true,
  };
}

export const customExecutiveMetricsService = {
  TILE_PLACEMENTS,
  TILE_PROVIDER_TYPES,

  invalidateExecutiveConfigCache,

  async getExecutiveConfig(organizationId, { forceRefresh = false, includeHistory = true } = {}) {
    if (!organizationId) {
      const local = readLocalStore(organizationId);
      return {
        tiles: local.tiles,
        layout: local.layout || {},
        branding: local.branding || {},
      };
    }

    const key = String(organizationId);
    if (!forceRefresh) {
      const cached = configCache.get(key);
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        return cached.data;
      }
    }

    if (inflightConfig.has(key)) {
      return inflightConfig.get(key);
    }

    const promise = (async () => {
      try {
        await migrateLocalToServer(organizationId);
        const data = await executiveApiService.getConfig(organizationId, { includeHistory });
        const normalized = {
          tiles: data.tiles || [],
          layout: data.layout || {},
          branding: data.branding || {},
        };
        configCache.set(key, { data: normalized, fetchedAt: Date.now() });
        return normalized;
      } catch {
        try {
          const [metrics, layoutData] = await Promise.all([
            executiveApiService.getCustomMetrics(organizationId, { includeHistory }),
            executiveApiService.getLayout(organizationId),
          ]);
          const normalized = {
            tiles: metrics.tiles || [],
            layout: layoutData.layout || {},
            branding: layoutData.branding || {},
          };
          configCache.set(key, { data: normalized, fetchedAt: Date.now() });
          return normalized;
        } catch {
          const local = readLocalStore(organizationId);
          return {
            tiles: local.tiles,
            layout: local.layout || {},
            branding: local.branding || {},
          };
        }
      } finally {
        inflightConfig.delete(key);
      }
    })();

    inflightConfig.set(key, promise);
    return promise;
  },

  async getCustomMetrics(organizationId, { includeHistory = true } = {}) {
    const config = await this.getExecutiveConfig(organizationId, { includeHistory });
    return { tiles: config.tiles, layout: config.layout };
  },

  async saveCustomTile(organizationId, tile) {
    const entry = normalizeTile({
      ...tile,
      id: tile.id || makeId('tile'),
      label: tile.label?.trim(),
      value: tile.value != null && tile.value !== '' ? Number(tile.value) : null,
      compareValue: tile.compareValue != null && tile.compareValue !== '' ? Number(tile.compareValue) : null,
      sourceLabel: tile.sourceLabel?.trim() || 'Custom data',
      updatedAt: new Date().toISOString(),
    });

    if (!organizationId) {
      const store = readLocalStore(organizationId);
      const idx = store.tiles.findIndex((t) => t.id === entry.id);
      if (idx >= 0) store.tiles[idx] = entry;
      else store.tiles.push(entry);
      writeLocalStore(organizationId, store);
      notifyUpdated(organizationId);
      return entry;
    }

    try {
      const saved = await executiveApiService.upsertCustomMetric(organizationId, entry);
      notifyUpdated(organizationId);
      return normalizeTile(saved);
    } catch {
      const store = readLocalStore(organizationId);
      const idx = store.tiles.findIndex((t) => t.id === entry.id);
      if (idx >= 0) store.tiles[idx] = entry;
      else store.tiles.push(entry);
      writeLocalStore(organizationId, store);
      notifyUpdated(organizationId);
      return entry;
    }
  },

  async removeCustomTile(organizationId, tileId) {
    if (!organizationId) {
      const store = readLocalStore(organizationId);
      store.tiles = store.tiles.filter((t) => t.id !== tileId);
      writeLocalStore(organizationId, store);
      notifyUpdated(organizationId);
      return;
    }
    try {
      await executiveApiService.deleteCustomMetric(organizationId, tileId);
    } catch {
      const store = readLocalStore(organizationId);
      store.tiles = store.tiles.filter((t) => t.id !== tileId);
      writeLocalStore(organizationId, store);
    }
    notifyUpdated(organizationId);
  },

  async getLayoutOverrides(organizationId, { includeHistory = true } = {}) {
    const config = await this.getExecutiveConfig(organizationId, { includeHistory });
    return { layout: config.layout, branding: config.branding };
  },

  async saveLayoutOverrides(organizationId, layout, branding) {
    if (!organizationId) {
      const store = readLocalStore(organizationId);
      if (layout != null) store.layout = { ...store.layout, ...layout };
      if (branding != null) store.branding = { ...store.branding, ...branding };
      writeLocalStore(organizationId, store);
      notifyUpdated(organizationId);
      return { layout: store.layout, branding: store.branding };
    }
    try {
      const saved = await executiveApiService.saveLayout(organizationId, { layout, branding });
      notifyUpdated(organizationId);
      return saved;
    } catch {
      const store = readLocalStore(organizationId);
      if (layout != null) store.layout = { ...store.layout, ...layout };
      if (branding != null) store.branding = { ...store.branding, ...branding };
      writeLocalStore(organizationId, store);
      notifyUpdated(organizationId);
      return { layout: store.layout, branding: store.branding };
    }
  },

  /** Merge custom tiles into executive dashboard payload shape */
  async applyCustomMetrics(payload, organizationId) {
    const { tiles } = await this.getExecutiveConfig(organizationId);
    return applyTilesToPayload(payload, tiles);
  },
};
